/**
 * The signed-in teammate's session timer: this session and today's total.
 *
 * The figures tick in the browser (`now` advances every 15 s, see
 * app-js/session.js) from a baseline the server gives (`sessions/current/`), so
 * the profile chips stay live without polling. Labels never show seconds.
 *
 * `deadline` is the shared idle-logout moment (epoch ms) every tab counts down
 * to; null when this teammate is never auto-logged-out.
 */
import { formatHM } from "@/utils/duration";

const SESSION_KEY = "orrish.session";

const empty = () => ({
  tracked: false,
  timeoutMinutes: 10,
  key: null,
  startedAt: null,
  deadline: null,
  // `sessions/current/` today block, and the browser time it was fetched at.
  today: null,
  todayAt: 0,
  now: Date.now(),
});

function readSaved() {
  try {
    const saved = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
    return saved && typeof saved === "object" ? saved : null;
  } catch (e) {
    return null;
  }
}

export default {
  namespaced: true,
  state: empty,
  getters: {
    tracked: (state) => state.tracked,
    timeoutMinutes: (state) => state.timeoutMinutes,
    deadline: (state) => state.deadline,
    currentSeconds: (state) =>
      state.startedAt ? Math.max(0, (state.now - state.startedAt) / 1000) : 0,
    todaySeconds: (state, getters) => {
      if (!state.today) return getters.currentSeconds;
      const live = state.today.is_online ? Math.max(0, (state.now - state.todayAt) / 1000) : 0;
      return (state.today.total_seconds || 0) + live;
    },
    currentLabel: (state, getters) => formatHM(getters.currentSeconds),
    todayLabel: (state, getters) => formatHM(getters.todaySeconds),
  },
  mutations: {
    HYDRATE(state, saved) {
      state.tracked = !!(saved && saved.tracked);
      state.timeoutMinutes = (saved && Number(saved.timeout_minutes)) || 10;
      state.key = (saved && saved.key) || null;
      state.startedAt = saved && saved.started_at ? new Date(saved.started_at).getTime() : null;
      state.now = Date.now();
    },
    SET_DEADLINE(state, value) {
      state.deadline = value || null;
    },
    SET_CURRENT(state, result) {
      const session = (result && result.session) || null;
      const policy = (result && result.policy) || null;
      if (policy) {
        state.tracked = !!policy.tracked;
        state.timeoutMinutes = Number(policy.timeout_minutes) || state.timeoutMinutes;
      }
      if (session && session.started_at) state.startedAt = new Date(session.started_at).getTime();
      state.today = (result && result.today) || null;
      state.todayAt = Date.now();
      state.now = Date.now();
    },
    TICK(state) {
      state.now = Date.now();
    },
    CLEAR(state) {
      Object.assign(state, empty());
    },
  },
  actions: {
    hydrate({ commit }) {
      commit("HYDRATE", readSaved());
    },
    /** Refresh the baseline from the server. Never rejects. */
    fetchCurrent({ commit }, { passive = true } = {}) {
      if (!localStorage.getItem("token")) return Promise.resolve();
      return Promise.all([import("@/app-js/new-base-axios"), import("@/app-js/api-urls")]).then(
        ([{ default: request }, { default: urls }]) =>
          new Promise((resolve) => {
            request("get", urls.SESSION_CURRENT, {
              passive,
              showErrorMessage: false,
              onSuccess: (res) => commit("SET_CURRENT", res.data.result),
              onFinally: resolve,
            });
          })
      );
    },
    clear({ commit }) {
      commit("CLEAR");
    },
  },
};
